<template>
    <Teleport
        :to="toTeleportId || scrollParent || '#app'"
        defer
        :disabled="disabledTeleport"
    >
        <transition
            name="tl-popover"
            @enter="onEnter"
            @after-leave="onAfterLeave"
        >
            <div
                v-if="props.keepAlive || modelValue"
                v-show="modelValue"
                ref="floatingRef"
                class="tl-popover-overlay"
                :style="overlayStyles"
            >
                <slot />
            </div>
        </transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, type CSSProperties, onMounted, onUnmounted, provide, ref, useId, watch } from 'vue'
import { onClickOutside, type OnClickOutsideHandler } from '@vueuse/core'
import { autoPlacement, autoUpdate, offset as offsetMiddleware, size, useFloating } from '@floating-ui/vue'
import useTeleport from 'widget-components/composables/teleport/useTeleport'
import { TELEPORT_ID_KEY } from 'widget-components/composables/teleport/consts'
import { getCssVar } from 'widget-components/utilities/getCssVar'
import { getScrollParent } from 'widget-components/utilities/get-scroll-parent'
import isIframe from 'widget-components/utilities/isIframe'

const props = withDefaults(defineProps<{
    reference?: HTMLElement | null
    offset?: number
    maxHeight?: number
    maxWidth?: number
    matchReferenceWidth?: boolean
    /** When true, keeps content in DOM when closed (preserves state). Default: false (destroys content) */
    keepAlive?: boolean
    /** When true, clicks on the reference element also close the popover. Default: false */
    closeOnReferenceClick?: boolean
}>(), {
    offset: 4,
    maxHeight: 400,
    matchReferenceWidth: false,
    keepAlive: false,
    closeOnReferenceClick: false,
})

const modelValue = defineModel<boolean>({ default: false })

const popoverId = useId()
const { toTeleportId } = useTeleport()

const floatingRef = ref<HTMLDivElement>()
const scrollParent = ref<Element | null>(null)
const disabledTeleport = ref(true)
const overlayZindex = ref<string>('-1')
const contentHeight = ref<number | null>(null)
const contentWidth = ref<number | null>(null)

// Teleporting can move this content outside the DOM subtree that received the
// theme's CSS variables, so these are snapshotted from the reference element's
// ancestor chain and reapplied directly on the overlay.
const inheritedCssVariables = ref<Record<string, string>>({})

const captureInheritedCssVariables = () => {
    if (!props.reference) {
        return
    }

    const result: Record<string, string> = {}
    let node: Element | null = props.reference

    while (node) {
        const style = (node as HTMLElement).style
        for (let i = 0; i < style.length; i++) {
            const name = style[i]
            if (name.startsWith('--tl-') && !(name in result)) {
                result[name] = style.getPropertyValue(name).trim()
            }
        }
        node = node.parentElement
    }

    inheritedCssVariables.value = result
}

const referenceRef = computed(() => props.reference)

const { floatingStyles, update, isPositioned } = useFloating(referenceRef, floatingRef, {
    placement: 'bottom-end',
    transform: false,
    open: modelValue,
    whileElementsMounted: autoUpdate,
    middleware: [
        offsetMiddleware(props.offset),
        autoPlacement({
            elementContext: 'reference',
            autoAlignment: false,
            allowedPlacements: ['top-end', 'bottom-end'],
        }),
        size({
            apply({ availableWidth, availableHeight, rects }) {
                contentHeight.value = Math.min(availableHeight, props.maxHeight)

                if (props.matchReferenceWidth) {
                    contentWidth.value = rects.reference.width
                } else if (props.maxWidth) {
                    contentWidth.value = Math.min(availableWidth, props.maxWidth)
                }
            },
        }),
        {
            name: 'fixTopPlacement',
            fn({ placement, rects }) {
                if (placement === 'top') {
                    // Use actual floating height (may be smaller than maxHeight if content is short)
                    const actualHeight = Math.min(rects.floating.height, contentHeight.value || Infinity)
                    const newY = rects.reference.y - actualHeight - props.offset
                    return { y: newY }
                }
                return {}
            },
        },
    ],
})

const overlayStyles = computed((): CSSProperties => ({
    ...inheritedCssVariables.value,
    ...floatingStyles.value,
    width: contentWidth.value ? `${contentWidth.value}px` : undefined,
    maxHeight: contentHeight.value ? `${contentHeight.value}px` : undefined,
    display: 'flex',
    flexDirection: 'column',
    zIndex: overlayZindex.value,
}))

let stopClickOutsideVueUse: (() => void) | undefined
let stopEscapeListener: (() => void) | undefined
let stopIframeBlurListener: (() => void) | undefined

const close = () => {
    modelValue.value = false
}

const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        event.stopPropagation()
        close()
    }
}

const handleIframeBlur = () => {
    close()
}

const cleanupListeners = () => {
    if (stopClickOutsideVueUse) {
        stopClickOutsideVueUse()
        stopClickOutsideVueUse = undefined
    }
    if (stopEscapeListener) {
        stopEscapeListener()
        stopEscapeListener = undefined
    }
    if (stopIframeBlurListener) {
        stopIframeBlurListener()
        stopIframeBlurListener = undefined
    }
}

watch(modelValue, (open) => {
    if (open) {
        captureInheritedCssVariables()

        stopClickOutsideVueUse = onClickOutside(
            props.closeOnReferenceClick ? floatingRef : referenceRef,
            close as OnClickOutsideHandler,
            {
                detectIframe: true,
                ...(!props.closeOnReferenceClick && { ignore: [floatingRef] }),
            },
        )

        document.addEventListener('keydown', handleEscape)
        stopEscapeListener = () => document.removeEventListener('keydown', handleEscape)

        if (isIframe) {
            window.addEventListener('blur', handleIframeBlur, { passive: true })
            stopIframeBlurListener = () => window.removeEventListener('blur', handleIframeBlur)
        }
    } else {
        cleanupListeners()
    }
})

const onEnter = () => {
    overlayZindex.value = getCssVar('--layer-component-popover')
}

const onAfterLeave = () => {
    overlayZindex.value = '-1'
}

onMounted(() => {
    if (props.reference) {
        scrollParent.value = getScrollParent(props.reference)
    }

    setTimeout(() => {
        disabledTeleport.value = false
    }, 0)
})

onUnmounted(() => {
    disabledTeleport.value = true
    cleanupListeners()
})

provide(TELEPORT_ID_KEY, popoverId)

defineExpose({
    isPositioned,
    update,
    floatingRef,
})
</script>

<style lang="sass" scoped>
.tl-popover-overlay
    --tl-container-bg-color: var(--tl-white)

.tl-popover-overlay > *
    border: 1px solid var(--tl-inputs-border-color)
    border-radius: var(--tl-inputs-border-radius)
    box-shadow: var(--tl-inputs-overlay-shadow)
    background: var(--tl-inputs-bg-color)

.tl-popover-enter-from, .tl-popover-leave-to
    opacity: 0

.tl-popover-enter-active, .tl-popover-leave-active
    transition: opacity .2s ease-in-out
</style>
